// Small navigation card that lives where the legacy ClassFinancialsSummary
// strip used to render. Pulls only the three trusted values for the current
// month from the same authoritative service the Financial Reports page uses.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { fetchMonthlyReport, money, type MonthlyReport } from "@/lib/financialReport";

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export default function FinancialReportsNavCard() {
  const nav = useNavigate();
  const [data, setData] = useState<MonthlyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fetchMonthlyReport({ period_type: "month", month: currentMonthKey() })
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <Card>
      <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-heading font-semibold text-foreground">Financial reporting</div>
          <div className="text-xs text-muted-foreground">
            View actual revenue, payroll, Grace accruals, and monthly net results.
          </div>
          {loading ? (
            <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading current month…
            </div>
          ) : error ? (
            <div className="mt-2 text-xs text-destructive flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3" /> Financial data unavailable
            </div>
          ) : data ? (
            <div className="mt-2 flex flex-wrap gap-4 text-xs">
              <MiniStat label="Net revenue collected" value={money(data.summary.net_revenue_cents)} />
              <MiniStat label="Payroll due" value={money(data.summary.total_payroll_cents)} />
              <MiniStat label={`Grace accrued (${data.summary.grace_percent}%)`} value={money(data.summary.grace_accrued_cents)} />
              <div className="text-[10px] text-muted-foreground self-end">
                {data.period.label}
              </div>
            </div>
          ) : null}
        </div>
        <Button size="sm" onClick={() => nav("/admin/reports")}>
          Open Financial Reports <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="tabular-nums font-medium">{value}</div>
    </div>
  );
}
